"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { Github, Linkedin, User, Twitter } from "lucide-react";
import { FaReddit, FaDiscord } from "react-icons/fa";

export type CustomerCardProps = {
  id: string;
  name: string;
  company: string;
  position: string;
  photo?: string;
  quote: string;
  social: {
    discord?: string;
    linkedin?: string;
    github?: string;
    reddit?: string;
    twitter?: string;
  };
};

export function CustomerCard({
  id,
  name,
  company,
  position,
  photo,
  quote,
  social,
}: CustomerCardProps) {
  const socialIcons = {
    discord: social.discord ? <FaDiscord className="h-5 w-5" /> : null,
    linkedin: social.linkedin ? <Linkedin className="h-5 w-5" /> : null,
    github: social.github ? <Github className="h-5 w-5" /> : null,
    reddit: social.reddit ? <FaReddit className="h-5 w-5" /> : null,
    twitter: social.twitter ? <Twitter className="h-5 w-5" /> : null,
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-card shadow overflow-hidden hover:shadow-lg transition-shadow"
    >
      <Link href={`/customers/${id}`} className="block">
        <div className="aspect-square w-full relative">
          {photo ? (
            <Image
              key={photo}
              src={photo}
              alt={name}
              fill
              className="object-cover"
              sizes="(max-width: 768px) 100vw, 33vw"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-accent/30">
              <User className="h-24 w-24 text-primary/40" />
            </div>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div>
            <h3 className="text-xl font-bold">{name}</h3>
            <p className="text-muted-foreground">
              {position}, {company}
            </p>
          </div>

          <blockquote className="italic text-sm border-l-4 border-primary/70 pl-3 py-1">
            &quot;{quote}&quot;
          </blockquote>

          <div className="flex gap-3 items-center pt-2">
            {social.twitter && <span>{socialIcons.twitter}</span>}
            {social.linkedin && <span> {socialIcons.linkedin}</span>}
            {social.github && <span> {socialIcons.github}</span>}
            {social.discord && <span>{socialIcons.discord}</span>}
            {social.reddit && <span>{socialIcons.reddit}</span>}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
